import React, { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Spinner } from "../components/shared/Spinner";
import {
	menuAPI,
	categoriesAPI,
	dealsAPI,
	paymentsAPI,
	tablesAPI,
} from "../services/api";
import { fmt, r3 } from "../components/shared/fmt";
import { useTitle } from "../hooks/useTitle";
import type { CartItem, Table } from "../types";

const TAX_RATE = 0.15;
const PM_LIST = [
	{ id: "cash", l: "Cash", i: "💵" },
	{ id: "knet", l: "KNET", i: "🏧" },
	{ id: "visa", l: "Visa", i: "💳" },
	{ id: "master", l: "Mastercard", i: "💳" },
	{ id: "amex", l: "Amex", i: "💳" },
	{ id: "qr", l: "QR Pay", i: "📱" },
];

function calcTotals(cart: CartItem[], discount: number) {
	const sub = r3(cart.reduce((s, i) => s + i.price * i.qty, 0));
	const disc = r3(Math.min(discount, sub));
	const tax = r3((sub - disc) * TAX_RATE);
	const total = r3(sub - disc + tax);
	return { sub, disc, tax, total };
}

// ── Table Selector ────────────────────────────────────────────────────────────
const TableSelector: React.FC<{ onSelect: (t: Table | null) => void }> = ({
	onSelect,
}) => {
	const { data, isLoading } = useQuery({
		queryKey: ["tables"],
		queryFn: () => tablesAPI.getAll(),
	});
	const tables = data?.data || [];
	const [fSec, setFSec] = useState("All");
	const sections = [
		"All",
		...Array.from(new Set(tables.map((t: any) => t.section))),
	];
	const visible = tables.filter(
		(t: any) => fSec === "All" || t.section === fSec,
	);

	if (isLoading) return <Spinner />;

	return (
		<div style={{ padding: 24 }}>
			<div className="page-title" style={{ marginBottom: 8 }}>
				🖥️ Open POS
			</div>
			<div className="page-sub">Select a table or start a quick order</div>

			<div className="card">
				<div className="card-title">Quick Start — No Table</div>
				<div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
					{[
						{ l: "🛍️ Takeaway", ot: "takeaway" },
						{ l: "🛵 Delivery", ot: "delivery" },
					].map((o) => (
						<div
							key={o.ot}
							style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<button
								className="btn-primary"
								style={{ padding: "12px 22px", fontSize: 13 }}
								onClick={() =>
									onSelect({
										id: `virt_${o.ot}`,
										label: o.l.split(" ")[1],
										section: o.ot,
										capacity: 0,
										status: "available",
										guests: 0,
										openedAt: null,
										reservedBy: null,
									})
								}>
								{o.l}
							</button>
							<span style={{ fontSize: 11, color: "var(--muted)" }}>
								{o.ot === "takeaway"
									? "Customer takes away"
									: "Deliver to address"}
							</span>
						</div>
					))}
				</div>
			</div>

			<div className="card">
				<div className="card-title">
					🪑 Dine In — Select Table
					<div className="filter-tabs" style={{ marginBottom: 0 }}>
						{sections?.map((res) => {
							return (
								<button
									key={String(res)}
									className={`filter-tab ${fSec === res ? "active" : ""}`}
									onClick={() => setFSec(String(res))}>
									{String(res)}
								</button>
							);
						})}
					</div>
				</div>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))",
						gap: 10,
					}}>
					{visible.map((t: any) => {
						const isRes = t.status === "reserved";
						const bColor =
							t.status === "occupied"
								? "rgba(239,68,68,.5)"
								: isRes
									? "rgba(168,85,247,.5)"
									: "var(--border)";
						const bgColor =
							t.status === "occupied"
								? "rgba(239,68,68,.04)"
								: isRes
									? "rgba(168,85,247,.04)"
									: "var(--card2)";
						return (
							<div
								key={t.id}
								style={{
									background: bgColor,
									borderRadius: "var(--r)",
									border: `2px solid ${bColor}`,
									padding: 14,
									textAlign: "center",
									position: "relative",
								}}>
								<div
									style={{
										position: "absolute",
										top: 6,
										right: 8,
										fontSize: 10,
										color: "var(--muted)",
										fontWeight: 600,
									}}>
									👥{t.capacity}
								</div>
								<div
									style={{
										fontFamily: "Syne,sans-serif",
										fontSize: 20,
										fontWeight: 800,
										marginBottom: 4,
									}}>
									{t.label}
								</div>
								<div
									style={{
										fontSize: 10,
										color: "var(--muted)",
										marginBottom: 6,
									}}>
									{t.section}
								</div>
								<span className={`badge badge-${t.status}`}>{t.status}</span>
								{!isRes ? (
									<button
										className="btn-primary"
										style={{
											width: "100%",
											marginTop: 8,
											fontSize: 11,
											padding: "7px 6px",
										}}
										onClick={() => onSelect(t)}>
										{t.status === "occupied"
											? "🧾 Add to Order"
											: "➕ New Order"}
									</button>
								) : (
									<div
										style={{
											marginTop: 8,
											fontSize: 11,
											color: "var(--purple)",
											fontWeight: 600,
										}}>
										🔒 {t.reservedBy || "Reserved"}
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

// ── POS Screen ────────────────────────────────────────────────────────────────
const POSScreen: React.FC<{ table: Table; onClose: () => void }> = ({
	table,
	onClose,
}) => {
	const { data: md } = useQuery({
		queryKey: ["menu"],
		queryFn: () => menuAPI.getAll(),
	});
	const { data: cd } = useQuery({
		queryKey: ["cats"],
		queryFn: () => categoriesAPI.getAll(),
	});
	const menu = md?.data || [];
	const cats = cd?.data || [];

	const [cart, setCart] = useState<CartItem[]>([]);
	const [activeCat, setActiveCat] = useState("all");
	const [search, setSearch] = useState("");
	const [orderType, setOrderType] = useState("dine_in");
	const [dealCode, setDealCode] = useState("");
	const [appliedDeal, setApplied] = useState<any>(null);
	const [discount, setDiscount] = useState(0);
	const [phase, setPhase] = useState<"order" | "pay" | "receipt">("order");
	const [payMethod, setPayMethod] = useState("");
	const [tendered, setTendered] = useState("");
	const [receipt, setReceipt] = useState<any>(null);

	const { sub, disc, tax, total } = calcTotals(cart, discount);

	const addItem = useCallback((item: any) => {
		if (!item.available) return;
		setCart((c) => {
			const ex = c.find((x) => x.id === item.id);
			if (ex)
				return c.map((x) => (x.id === item.id ? { ...x, qty: x.qty + 1 } : x));
			return [
				...c,
				{
					id: item.id,
					name: item.name,
					emoji: item.emoji,
					price: item.price,
					qty: 1,
				},
			];
		});
	}, []);

	const changeQty = (id: string, delta: number) => {
		setCart((c) =>
			c
				.map((x) => (x.id === id ? { ...x, qty: x.qty + delta } : x))
				.filter((x) => x.qty > 0),
		);
	};

	const applyDeal = async () => {
		if (!dealCode.trim()) return;
		try {
			const res = await dealsAPI.validate(dealCode.trim(), sub);
			setApplied(res.data.deal);
			setDiscount(res.data.discount);
			toast.success(`✅ Deal applied: ${res.data.deal.name}`);
		} catch {
			setApplied(null);
			setDiscount(0);
		}
	};

	const confirmPay = async () => {
		if (!payMethod) {
			toast.error("Select a payment method");
			return;
		}
		const tendNum = parseFloat(tendered) || 0;
		if (payMethod === "cash" && tendNum < total) {
			toast.error("Insufficient cash");
			return;
		}
		const change = payMethod === "cash" ? Math.max(0, tendNum - total) : 0;
		const rNo = `TKN-${String(Date.now()).slice(-6)}`;
		try {
			await paymentsAPI.process({
				orderId: rNo,
				method: payMethod as any,
				tendered: tendNum || total,
			});
		} catch {
			/* backend not connected — show receipt anyway */
		}
		setReceipt({
			rNo,
			table: table.label,
			orderType,
			items: [...cart],
			sub,
			disc,
			tax,
			total,
			method: payMethod,
			tendered: tendNum || total,
			change,
			dealName: appliedDeal?.name || "",
			time: new Date().toLocaleTimeString("en-KW", {
				hour: "2-digit",
				minute: "2-digit",
			}),
		});
		setPhase("receipt");
	};

	const quickCash = [total, Math.ceil(total), Math.ceil(total / 5) * 5, 20, 50]
		.filter((v, i, a) => v >= total && a.indexOf(v) === i)
		.sort((a, b) => a - b)
		.slice(0, 4);
	const visible = menu.filter(
		(i: any) =>
			(activeCat === "all" || i.catId === activeCat) &&
			(!search || i.name.toLowerCase().includes(search.toLowerCase())) &&
			i.available,
	);

	// ── Receipt phase ──────────────────────────────────────────────────────────
	if (phase === "receipt" && receipt) {
		const meths: Record<string, string> = {
			cash: "Cash",
			knet: "KNET",
			visa: "Visa",
			master: "Mastercard",
			amex: "Amex",
			qr: "QR Pay",
		};
		return (
			<div
				style={{
					flex: 1,
					overflowY: "auto",
					padding: 24,
					maxWidth: 480,
					margin: "0 auto",
					width: "100%",
				}}>
				<div
					style={{
						fontSize: 52,
						textAlign: "center",
						display: "block",
						marginBottom: 12,
					}}>
					✅
				</div>
				<div
					style={{
						fontFamily: "Syne,sans-serif",
						fontSize: 20,
						fontWeight: 800,
						textAlign: "center",
						marginBottom: 4,
					}}>
					Payment Successful!
				</div>
				<div
					style={{
						fontSize: 12,
						color: "var(--muted)",
						textAlign: "center",
						marginBottom: 20,
					}}>
					Order cleared for {receipt.table}
				</div>
				<div className="receipt-paper">
					<div className="receipt-hdr">
						<div className="receipt-brand">TEKNULUGUY</div>
						<div style={{ fontSize: 10, color: "var(--muted)", marginTop: 3 }}>
							Kuwait City, Kuwait
						</div>
					</div>
					{[
						[" Receipt #", receipt.rNo],
						["Table", receipt.table],
						["Order Type", receipt.orderType.replace("_", " ")],
						["Time", receipt.time],
					].map(([l, v]) => (
						<div key={l} className="rrow">
							<span>{l}</span>
							<span>{v}</span>
						</div>
					))}
					<div
						style={{
							height: 1,
							background: "var(--border)",
							borderStyle: "dashed",
							margin: "8px 0",
						}}
					/>
					{receipt.items.map((i: CartItem) => (
						<div key={i.id} className="rrow">
							<span>
								{i.name} ×{i.qty}
							</span>
							<span>{fmt(i.price * i.qty)}</span>
						</div>
					))}
					<div
						style={{
							height: 1,
							background: "var(--border)",
							borderStyle: "dashed",
							margin: "8px 0",
						}}
					/>
					{receipt.disc > 0 && (
						<div className="rrow">
							<span style={{ color: "var(--accentl)" }}>
								Discount{receipt.dealName ? ` (${receipt.dealName})` : ""}
							</span>
							<span style={{ color: "var(--accentl)" }}>
								-{fmt(receipt.disc)}
							</span>
						</div>
					)}
					<div className="rrow">
						<span>Tax (15%)</span>
						<span>{fmt(receipt.tax)}</span>
					</div>
					<div className="rrow total">
						<span>TOTAL</span>
						<span>{fmt(receipt.total)}</span>
					</div>
					<div
						style={{
							height: 1,
							background: "var(--border)",
							borderStyle: "dashed",
							margin: "8px 0",
						}}
					/>
					<div className="rrow">
						<span>Payment</span>
						<span>{meths[receipt.method] || receipt.method}</span>
					</div>
					{receipt.method === "cash" && (
						<>
							<div className="rrow">
								<span>Tendered</span>
								<span>{fmt(receipt.tendered)}</span>
							</div>
							<div className="rrow bold">
								<span>Change</span>
								<span>{fmt(receipt.change)}</span>
							</div>
						</>
					)}
					<div className="receipt-footer">
						Thank you for dining with us!
						<br />
						شكراً لزيارتكم — طعام شهي
					</div>
				</div>
				<div style={{ display: "flex", gap: 10 }}>
					<button className="pos-charge-btn" onClick={() => window.print()}>
						🖨 Print Receipt
					</button>
					<button
						className="pos-charge-btn"
						onClick={() => {
							setCart([]);
							setApplied(null);
							setDiscount(0);
							setPayMethod("");
							setTendered("");
							setPhase("order");
						}}>
						✨ New Order
					</button>
				</div>
				<button
					className="pos-clear-btn"
					style={{ marginTop: 8 }}
					onClick={onClose}>
					← Back to Dashboard
				</button>
			</div>
		);
	}

	// ── Payment phase ──────────────────────────────────────────────────────────
	if (phase === "pay") {
		const tendNum = parseFloat(tendered) || 0;
		const change = tendNum - total;
		return (
			<div
				style={{
					flex: 1,
					overflowY: "auto",
					padding: 22,
					maxWidth: 560,
					margin: "0 auto",
					width: "100%",
				}}>
				<div className="page-title" style={{ marginBottom: 16 }}>
					💳 Process Payment
				</div>

				{/* Summary */}
				<div className="card">
					<div className="card-title" style={{ marginBottom: 10 }}>
						Order Summary
					</div>
					{cart.map((i) => (
						<div key={i.id} className="rrow">
							<span>
								{i.emoji} {i.name} ×{i.qty}
							</span>
							<span>{fmt(i.price * i.qty)}</span>
						</div>
					))}
					<div
						style={{ height: 1, background: "var(--border)", margin: "8px 0" }}
					/>
					{disc > 0 && (
						<div className="rrow">
							<span style={{ color: "var(--accentl)" }}>Discount</span>
							<span style={{ color: "var(--accentl)" }}>-{fmt(disc)}</span>
						</div>
					)}
					<div className="rrow">
						<span style={{ color: "var(--amber)" }}>Tax (15%)</span>
						<span style={{ color: "var(--amber)" }}>{fmt(tax)}</span>
					</div>
					<div className="rrow total">
						<span>Total Due</span>
						<span>{fmt(total)}</span>
					</div>
				</div>

				{/* Methods */}
				<div className="card">
					<div className="card-title" style={{ marginBottom: 12 }}>
						Payment Method
					</div>
					<div className="pay-methods">
						{PM_LIST.map((m) => (
							<div
								key={m.id}
								className={`pay-m${payMethod === m.id ? " active" : ""}`}
								onClick={() => {
									setPayMethod(m.id);
									setTendered("");
								}}>
								<span className="pay-m-icon">{m.i}</span>
								<span className="pay-m-label">{m.l}</span>
							</div>
						))}
					</div>
				</div>

				{/* Cash change calculator */}
				{payMethod === "cash" && (
					<div className="change-box">
						<div
							style={{
								fontSize: 11,
								fontWeight: 700,
								color: "var(--muted)",
								textTransform: "uppercase",
								letterSpacing: ".4px",
								marginBottom: 10,
							}}>
							💵 Change Calculator
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								marginBottom: 8,
							}}>
							<span
								style={{
									fontSize: 12,
									color: "var(--muted)",
									fontWeight: 600,
									whiteSpace: "nowrap",
								}}>
								Cash Tendered (KWD)
							</span>
							<input
								className="cash-inp"
								type="number"
								step="0.001"
								placeholder={total.toFixed(3)}
								value={tendered}
								onChange={(e) => setTendered(e.target.value)}
							/>
						</div>
						<div className="quick-cash">
							{quickCash.map((v) => (
								<button
									key={v}
									className="btn-secondary"
									style={{ fontSize: 11 }}
									onClick={() => setTendered(v.toFixed(3))}>
									{fmt(v)}
								</button>
							))}
						</div>
						{tendered && (
							<div className={`change-result${change < 0 ? " neg" : ""}`}>
								<span
									style={{
										fontSize: 12,
										color: "var(--muted)",
										fontWeight: 600,
									}}>
									{change >= 0 ? "Change to Return" : "Still Owed"}
								</span>
								<span className={`change-amt${change < 0 ? " neg" : ""}`}>
									{fmt(Math.abs(change))}
								</span>
							</div>
						)}
					</div>
				)}

				<div style={{ display: "flex", gap: 10 }}>
					<button
						className="btn-secondary"
						style={{ flex: 1, padding: 12 }}
						onClick={() => setPhase("order")}>
						← Back
					</button>
					<button
						className="pos-charge-btn"
						style={{ flex: 2, marginTop: 0 }}
						disabled={
							!payMethod ||
							(payMethod === "cash" && tendNum < total && !!tendered)
						}
						onClick={confirmPay}>
						✅ Confirm — {fmt(total)}
					</button>
				</div>
			</div>
		);
	}

	// ── Order phase ────────────────────────────────────────────────────────────
	return (
		<div className="pos-body">
			{/* Category sidebar */}
			<div className="pos-cats">
				<button
					className={`pos-cat-btn${activeCat === "all" ? " active" : ""}`}
					onClick={() => setActiveCat("all")}>
					<span className="pos-cat-em">🌟</span>
					<span className="pos-cat-lbl">All</span>
				</button>
				{cats.map((c: any) => (
					<button
						key={c.id}
						className={`pos-cat-btn${activeCat === c.id ? " active" : ""}`}
						onClick={() => setActiveCat(c.id)}>
						<span className="pos-cat-em">{c.icon}</span>
						<span className="pos-cat-lbl">{c.name}</span>
					</button>
				))}
			</div>

			{/* Menu grid */}
			<div className="pos-menu">
				<div className="pos-search-bar">
					<input
						className="pos-search"
						placeholder="🔍 Search menu…"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						autoFocus
					/>
				</div>
				<div className="pos-items">
					{visible.length === 0 && (
						<div className="pos-empty">
							<span style={{ fontSize: 36, opacity: 0.3 }}>🔍</span>
							<span style={{ fontWeight: 600 }}>No items found</span>
						</div>
					)}
					{visible.map((item: any) => {
						const inCart = cart.find((c) => c.id === item.id);
						return (
							<div
								key={item.id}
								className={`pos-item${inCart ? " in-cart" : ""}`}
								onClick={() => addItem(item)}>
								{inCart && <div className="pos-item-qty">{inCart.qty}</div>}
								<span className="pos-item-em">{item.emoji}</span>
								<div className="pos-item-name">{item.name}</div>
								<div className="pos-item-price">{fmt(item.price)}</div>
							</div>
						);
					})}
				</div>
			</div>

			{/* Cart */}
			<div className="pos-cart">
				<div className="pos-cart-hdr">
					<div className="pos-cart-title">Order</div>
					<div className="pos-ot-btns">
						{[
							{ id: "dine_in", l: "🪑 Dine In" },
							{ id: "takeaway", l: "🛍 Takeaway" },
							{ id: "delivery", l: "🛵 Delivery" },
						].map((ot) => (
							<button
								key={ot.id}
								className={`pos-ot${orderType === ot.id ? " active" : ""}`}
								onClick={() => setOrderType(ot.id)}>
								{ot.l}
							</button>
						))}
					</div>
				</div>

				<div className="pos-cart-items">
					{cart.length === 0 && (
						<div className="pos-empty">
							<span style={{ fontSize: 32, opacity: 0.3 }}>🛒</span>
							<span style={{ fontWeight: 600 }}>Empty cart</span>
							<span style={{ fontSize: 11 }}>Tap menu items to add</span>
						</div>
					)}
					{cart.map((item) => (
						<div key={item.id} className="pos-ci">
							<span className="pos-ci-em">{item.emoji}</span>
							<div className="pos-ci-info">
								<div className="pos-ci-name">{item.name}</div>
								<div className="pos-ci-price">{fmt(item.price * item.qty)}</div>
							</div>
							<div className="pos-qty-ctrl">
								<button
									className="pos-qb"
									onClick={() => changeQty(item.id, -1)}>
									−
								</button>
								<span className="pos-qn">{item.qty}</span>
								<button
									className="pos-qb"
									onClick={() => changeQty(item.id, 1)}>
									+
								</button>
							</div>
						</div>
					))}
				</div>

				<div className="pos-cart-foot">
					{/* Deal */}
					{!appliedDeal ? (
						<div className="pos-deal-row">
							<input
								className="pos-deal-inp"
								placeholder="🏷 Promo code"
								value={dealCode}
								onChange={(e) => setDealCode(e.target.value.toUpperCase())}
								onKeyDown={(e) => e.key === "Enter" && applyDeal()}
							/>
							<button className="pos-deal-btn" onClick={applyDeal}>
								Apply
							</button>
						</div>
					) : (
						<div className="pos-applied">
							<span>
								{appliedDeal.emoji} {appliedDeal.name} —{" "}
								{appliedDeal.discountType === "percent"
									? `${appliedDeal.discount}%`
									: fmt(appliedDeal.discount)}{" "}
								off
							</span>
							<button
								style={{
									background: "transparent",
									border: "none",
									color: "var(--red)",
									fontSize: 16,
									cursor: "pointer",
								}}
								onClick={() => {
									setApplied(null);
									setDiscount(0);
									setDealCode("");
								}}>
								✕
							</button>
						</div>
					)}

					{/* Totals */}
					<div className="pos-tr">
						<span>Subtotal</span>
						<span>{fmt(sub)}</span>
					</div>
					{disc > 0 && (
						<div className="pos-tr disc">
							<span>Discount</span>
							<span>-{fmt(disc)}</span>
						</div>
					)}
					<div className="pos-tr tax">
						<span>Tax (15%)</span>
						<span>{fmt(tax)}</span>
					</div>
					<div className="pos-tr grand">
						<span>Total</span>
						<span>{fmt(total)}</span>
					</div>

					<button
						className="pos-charge-btn"
						disabled={cart.length === 0}
						onClick={() => setPhase("pay")}>
						💳 Charge {fmt(total)}
					</button>
					{cart.length > 0 && (
						<button
							className="pos-clear-btn"
							onClick={() => {
								setCart([]);
								setApplied(null);
								setDiscount(0);
							}}>
							🗑 Clear Order
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

// ── Main POS Page ─────────────────────────────────────────────────────────────
export const POSPage: React.FC = () => {
	useTitle("POS");
	const [table, setTable] = useState<Table | null>(null);

	return (
		<div
			className="pos-overlay"
			style={{ position: "relative", height: "100%" }}>
			{!table ? (
				<div style={{ flex: 1, overflowY: "auto" }}>
					<TableSelector onSelect={setTable} />
				</div>
			) : (
				<>
					<div className="pos-topbar">
						<button className="btn-secondary" onClick={() => setTable(null)}>
							← Back
						</button>
						<div className="pos-title">POS — New Order</div>
						<div className="pos-tbl-badge">
							{table.label} · {table.section}
						</div>
					</div>
					<div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
						<POSScreen table={table} onClose={() => setTable(null)} />
					</div>
				</>
			)}
		</div>
	);
};
